import { Typography } from "@mui/material";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  MouseEvent,
  ChangeEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { usePageTitle } from "../../common/hooks/usePageTitle";
import AdminOrderList from "../../components/AdminOrderList/AdminOrderList";
import { OrderDetail } from "../../types";
import OrderService from "../../service/OrderService";
import { Box, FlexColBox } from "../../common/components";
import { spacing } from "../../styles/theme";
import { SortBy } from "../../common/components/Table/Table";

const AdminOrderListContainer = () => {
  const { t } = useTranslation();
  usePageTitle("pages.all_orders.title");

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sortBy, setSortBy] = useState<SortBy>();
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);

  const [preorders, setPreorders] = useState<{
    count: number;
    preOrders: OrderDetail[];
  }>({ count: 0, preOrders: [] });

  const fetchPreOrders = useCallback(async () => {
    setIsRefetching(true);
    const res = await OrderService.getPurchasedOrders(
      currentPage,
      rowsPerPage,
      searchTerm,
      sortBy
    );

    setPreorders(res.data.data);
    setIsRefetching(false);
    setIsInitialLoading(false);
  }, [currentPage, rowsPerPage, searchTerm, sortBy]);

  const totalNumberOfPage = useMemo(() => {
    return Math.ceil(preorders.count / rowsPerPage) || 0;
  }, [preorders.count, rowsPerPage]);

  const setPage = useCallback(
    (_: MouseEvent<HTMLButtonElement> | null, pageNumber: number) => {
      if (pageNumber <= 0) {
        setCurrentPage(0);
      } else if (pageNumber >= totalNumberOfPage) {
        setCurrentPage(totalNumberOfPage);
      } else {
        setCurrentPage(pageNumber);
      }
    },
    [totalNumberOfPage]
  );

  const handleSearch = (searchTerm: string) => {
    setCurrentPage(0);
    setSearchTerm(searchTerm);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = isNaN(Number(event.target.value))
      ? 50
      : Number(event.target.value);
    setRowsPerPage(newValue);
    setCurrentPage(0);
  };

  useEffect(() => {
    fetchPreOrders();
  }, [fetchPreOrders]);

  return (
    <FlexColBox
      gap={spacing.groupingDesktop}
      p={`${spacing.regularDesktop} ${spacing.largeDesktop} ${spacing.largeDesktop}`}
      fullWidth
    >
      <Typography variant="h2">{t("all_orders.title")}</Typography>
      <Box>
        <AdminOrderList
          ordersDetails={preorders.preOrders}
          totalCount={preorders.count}
          rowsPerPage={rowsPerPage}
          page={currentPage}
          handleChangePage={setPage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleSearch={handleSearch}
          handleSort={setSortBy}
          sortBy={sortBy}
          isRefetching={isRefetching}
          isInitialLoading={isInitialLoading}
        />
      </Box>
    </FlexColBox>
  );
};

export default AdminOrderListContainer;
